import React, { useState } from "react";
import termImg from "../../../assets/term-life.jpg";
import SEO from "../../../components/SEO";

import img10 from "../../../assets/img10.jpg";
import { Plus, Minus } from "lucide-react";
import BlogSection from "../../../components/BlogSection";

const TermLifeInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const siteContainer = "mx-auto w-full max-w-[1380px]";

  const faqs = [
    {
      question: "Qu'est-ce que l'assurance vie temporaire ?",
      answer:
        "L'assurance vie temporaire offre une couverture pour une période déterminée (p. ex. 10, 20 ou 30 ans), versant une prestation de décès aux bénéficiaires si l'assuré décède pendant cette période. C'est une option simple et abordable pour ceux qui cherchent une couverture d'assurance vie de base.",
    },
    {
      question: "Pourquoi l'assurance vie temporaire est-elle populaire à Brampton ?",
      answer:
        "À Brampton, l'assurance vie temporaire est privilégiée pour son accessibilité financière et sa souplesse, ce qui la rend idéale pour les familles et les personnes ayant des engagements financiers spécifiques comme les hypothèques ou les frais d'études.",
    },
    {
      question: "Quels types d'assurance vie temporaire sont disponibles ?",
      answer:
        "Les types courants comprennent l'assurance temporaire à niveau (primes et prestation de décès fixes), l'assurance temporaire décroissante (prestation de décès qui diminue au fil du temps) et l'assurance temporaire convertible (option de conversion en assurance permanente sans examen médical).",
    },
    {
      question: "Puis-je convertir mon assurance vie temporaire en police permanente ?",
      answer:
        "Oui, de nombreuses polices d'assurance vie temporaire offrent une option de conversion, vous permettant de passer à une police d'assurance vie permanente sans passer d'examen médical, offrant ainsi une couverture à long terme.",
    },
    {
      question: "Comment choisir le bon plan d'assurance vie temporaire ?",
      answer:
        "Choisir le bon plan implique d'évaluer vos besoins financiers, la durée du terme et le montant de la couverture. Consulter des experts en assurance, comme ceux d'einsured.ca, peut vous aider à adapter un plan à votre situation particulière.",
    },
  ];

  return (
    <>
      <SEO
        title="Assurance-vie temporaire Canada | Plans flexibles – einsured.ca"
        description="einsured.ca aide les Canadiens à comprendre l'assurance vie temporaire et à choisir des plans flexibles adaptés à leurs besoins et à leur budget."
        keywords="Assurance vie temporaire Canada, assurance vie abordable, assurance protection hypothécaire, assurance vie temporaire à niveau"
      />

      {/* HERO SECTION */}
      <section
        className="relative min-h-[60vh] sm:min-h-[70vh] md:min-h-[80vh] bg-cover bg-center flex items-center justify-center md:justify-start px-4 sm:px-6 md:px-20"
        style={{ backgroundImage: `url(${termImg})` }}
        role="img"
        aria-label="Bannière principale d'assurance vie temporaire"
      >
        <div className="absolute inset-0 bg-black/40"></div>

        <div className={`relative z-10 ${siteContainer}`}>
          <div className="max-w-xl text-center md:text-left text-white">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-3 md:mb-4">
              Assurance vie temporaire
            </h1>
            <p className="text-lg sm:text-xl font-semibold mb-5 md:mb-6">
              Couverture flexible pour 10, 20, 30 ans ou plus, taux garantis et versement sur lequel vos proches peuvent compter
            </p>
            <a
              href="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto inline-block bg-sky-400 hover:bg-sky-500 text-white font-semibold px-8 py-3 rounded-full transition text-center"
            >
              Sécuriser maintenant
            </a>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="px-4 sm:px-6 md:px-24 py-14 sm:py-16 bg-white text-gray-800 leading-relaxed">
        <div className={`${siteContainer}`}>
          {/* What is Term life insurance? */}
          <div className="max-w-5xl mx-auto mb-14 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-center text-[#49628b] mb-6 sm:mb-8">
              Qu'est-ce que l'assurance vie temporaire ?
            </h2>

            <p className="mb-4">
              <span className="text-sky-600 font-semibold">
                L'assurance vie temporaire
              </span>{" "}
              est un type de police d'assurance qui offre une couverture pour une période
              fixe plutôt que pour toute la durée de vie d'un individu.
            </p>

            <p className="mb-4">
              En général, la police dure entre 10 et 40 ans, et en cas de décès malheureux
              de l'assuré, les bénéficiaires reçoivent une prestation de décès. L'un des
              principaux avantages de cette police est son accessibilité financière, car elle
              n'inclut aucune composante de placement qui pourrait s'ajouter au coût. Ainsi,
              l'{" "}
              <span className="text-sky-600 font-semibold">
                assurance vie temporaire au Canada
              </span>{" "}
              offre une option simple et directe pour ceux qui cherchent une couverture
              d'assurance vie de base sans se soucier des coûts supplémentaires associés aux
              placements.
            </p>

            <p className="mb-0 sm:mb-4">
              Cette police est idéale pour les personnes qui souhaitent s'assurer que leurs
              proches sont pris en charge en cas de décès prématuré, sans avoir à se
              préoccuper de placements financiers complexes. De plus, l'assurance vie
              temporaire peut offrir la tranquillité d'esprit à ceux qui ont des dettes ou
              des personnes à charge qui ont besoin d'un soutien financier.
            </p>
          </div>

          {/* Affordability of Life insurance */}
          <div className="max-w-5xl mx-auto text-gray-800">
            <h2 className="text-2xl sm:text-3xl font-bold text-center text-[#49628b] mb-6 sm:mb-8">
              Accessibilité financière de l'assurance vie
            </h2>

            <p className="mb-4">
              En matière d'assurance vie, les{" "}
              <span className="text-sky-600 font-semibold">
                polices d'assurance temporaire
              </span>{" "}
              ont gagné en popularité grâce à leur accessibilité financière et leur rapport
              qualité-prix. Non seulement ces polices offrent une couverture simple, mais
              leurs primes sont souvent inférieures à d'autres types d'assurance, permettant
              aux individus de bénéficier d'une protection adéquate sans mettre à rude
              épreuve leurs finances. Cet avantage est particulièrement bénéfique pour les
              jeunes familles, les personnes ayant des obligations financières importantes
              ou toute personne cherchant à respecter un budget.
            </p>

            <p className="mb-8 sm:mb-10">
              En effet, l'assurance vie temporaire au Canada est devenue une option très
              recherchée pour ceux qui souhaitent assurer la stabilité financière de leurs
              proches en cas d'événement malheureux.
            </p>

            <div className="flex justify-center">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto bg-sky-500 hover:bg-sky-600 text-white font-semibold px-8 py-3 rounded-full transition text-center inline-block"
              >
                Commencer
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* TYPES OF TERM LIFE */}
      <section
        className="relative py-16 sm:py-20 md:py-28 px-4 sm:px-6 md:px-10 bg-cover bg-center md:bg-fixed"
        style={{ backgroundImage: `url(${img10})` }}
        role="img"
        aria-label="Image de fond des types d'assurance vie temporaire"
      >
        <div className="absolute inset-0 bg-sky-900/60"></div>

        <div className={`relative z-10 ${siteContainer} text-white`}>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-3">
            Types d'assurance vie temporaire
          </h2>
          <p className="text-center max-w-4xl mx-auto mb-6 md:mb-8 leading-relaxed text-gray-100">
            L'assurance vie temporaire existe sous diverses formes pour répondre à différents besoins et préférences.
          </p>
          <h3 className="text-xl sm:text-2xl md:text-[28px] text-center font-bold mb-8 md:mb-10 text-[#20a7df]">
            Voici quelques types courants d'assurance vie temporaire :
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-white/95 rounded-3xl shadow-lg p-6 sm:p-8 leading-relaxed text-gray-800 hover:shadow-xl transition">
              <h4 className="text-[#49628b] text-lg sm:text-xl font-bold text-center mb-3 sm:mb-4">
                Assurance vie temporaire à niveau :
              </h4>
              <p>
                C'est le type d'assurance vie temporaire le plus simple. La prestation de
                décès reste la même (à niveau) pendant toute la durée de la police. Les
                primes restent également constantes, offrant prévisibilité et stabilité.
              </p>
            </div>

            <div className="bg-white/95 rounded-3xl shadow-lg p-6 sm:p-8 leading-relaxed text-gray-800 hover:shadow-xl transition">
              <h4 className="text-[#49628b] text-lg sm:text-xl font-bold text-center mb-3 sm:mb-4">
                Assurance vie temporaire décroissante :
              </h4>
              <p>
                Dans l'assurance{" "}
                <span className="text-sky-600 font-semibold">vie temporaire décroissante,</span>{" "}
                la prestation de décès diminue pendant la durée de la police, tandis que les
                primes restent généralement constantes. Ce type de police est souvent choisi
                pour couvrir des obligations financières spécifiques qui diminuent avec le
                temps, comme une hypothèque.
              </p>
            </div>

            <div className="bg-white/95 rounded-3xl shadow-lg p-6 sm:p-8 leading-relaxed text-gray-800 hover:shadow-xl transition">
              <h4 className="text-[#49628b] text-lg sm:text-xl font-bold text-center mb-3 sm:mb-4">
                Assurance vie temporaire croissante (ou renouvelable) :
              </h4>
              <p>
                Avec l'assurance vie temporaire croissante, la prestation de décès augmente
                au fil du temps, généralement à des intervalles prédéterminés ou en fonction
                de l'inflation. Les primes peuvent également augmenter à mesure que le
                montant de la couverture s'accroît.
              </p>
            </div>

            <div className="bg-white/95 rounded-3xl shadow-lg p-6 sm:p-8 leading-relaxed text-gray-800 hover:shadow-xl transition">
              <h4 className="text-[#49628b] text-lg sm:text-xl font-bold text-center mb-3 sm:mb-4">
                Assurance vie temporaire convertible :
              </h4>
              <p>
                L'assurance vie temporaire convertible au Canada permet aux titulaires de
                police de convertir leur police temporaire en une police d'assurance vie
                permanente (comme l'assurance vie entière ou universelle) sans passer
                d'examen médical. Cela offre une flexibilité aux personnes souhaitant
                prolonger leur couverture au-delà de la durée initiale.
              </p>
            </div>

            <div className="bg-white/95 rounded-3xl shadow-lg p-6 sm:p-8 leading-relaxed text-gray-800 hover:shadow-xl transition">
              <h4 className="text-[#49628b] text-lg sm:text-xl font-bold text-center mb-3 sm:mb-4">
                Assurance vie temporaire renouvelable annuellement (TRA) :
              </h4>
              <p>
                L'assurance vie TRA offre une couverture pour un an à la fois, avec l'option
                de renouveler pour les années suivantes. Les primes augmentent généralement
                chaque année en fonction de l'âge et de la santé du titulaire. Bien que
                renouvelable, ce type d'assurance temporaire peut devenir coûteux au fil du
                temps.
              </p>
            </div>

            <div className="bg-white/95 rounded-3xl shadow-lg p-6 sm:p-8 leading-relaxed text-gray-800 hover:shadow-xl transition">
              <h4 className="text-[#49628b] text-lg sm:text-xl font-bold text-center mb-3 sm:mb-4">
                Assurance vie temporaire conjointe :
              </h4>
              <p>
                L'assurance vie temporaire conjointe couvre deux personnes, généralement des
                conjoints, sous une seule police. La prestation de décès est versée au décès
                de l'un ou l'autre des assurés. C'est une option rentable pour les couples
                cherchant à obtenir une couverture.
              </p>
            </div>

            <div className="bg-white/95 rounded-3xl shadow-lg p-6 sm:p-8 md:p-10 leading-relaxed md:col-span-3 text-gray-800 hover:shadow-xl transition">
              <h4 className="text-[#49628b] text-lg sm:text-xl font-bold text-center mb-3 sm:mb-4">
                Assurance vie temporaire collective :
              </h4>
              <p className="max-w-5xl mx-auto text-center">
                L'assurance vie temporaire collective est généralement offerte par les
                employeurs ou les organisations, fournissant une couverture à un groupe
                d'individus. Il peut s'agir d'une option plus abordable, mais la couverture
                prend généralement fin si la personne quitte le groupe ou l'emploi.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* DIFFERENCES */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 md:px-20 bg-white text-gray-800">
        <div className={`${siteContainer}`}>
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-[#49628b] mb-5 sm:mb-6">
              Différences entre l'assurance vie temporaire et l'assurance vie permanente
            </h2>

            <p className="text-center text-gray-600 max-w-4xl mx-auto mb-10 sm:mb-12 leading-relaxed">
              Lors du choix d'une police d'assurance vie temporaire, il est important de
              tenir compte de vos besoins spécifiques, de votre budget et de vos préférences.
              Chaque type d'assurance temporaire a ses propres avantages et limites. Il est
              recommandé de comparer soigneusement les options et de consulter un conseiller
              financier ou un professionnel de l'assurance pour prendre une décision éclairée
              en fonction de votre situation unique.
            </p>

            <div className="overflow-x-auto">
              <table className="min-w-[780px] w-full border border-gray-300 text-left text-gray-700 text-sm md:text-base">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-4 font-bold text-[#49628b] w-[20%]">Caractéristiques</th>
                    <th className="p-4 font-bold text-[#49628b] w-[40%]">Assurance vie temporaire</th>
                    <th className="p-4 font-bold text-[#49628b] w-[40%]">Assurance permanente</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t">
                    <td className="p-4 font-semibold text-[#49628b]">Durée de couverture</td>
                    <td className="p-4">
                      <span className="font-semibold text-[#20a7df]">L'assurance temporaire</span> offre
                      une couverture pour une durée spécifique, comme 10, 20, 25, 30 ou 40 ans. Un
                      produit temporaire jusqu'à l'âge de 100 ans est également disponible.
                    </td>
                    <td className="p-4">
                      <span className="font-semibold text-[#20a7df]">L'assurance permanente</span>{" "}
                      offre une couverture pour toute la vie de l'assuré.
                    </td>
                  </tr>

                  <tr className="border-t">
                    <td className="p-4 font-semibold text-[#49628b]">Primes</td>
                    <td className="p-4">
                      <span className="font-semibold text-[#20a7df]">Les primes d'assurance temporaire</span>{" "}
                      sont généralement plus abordables, surtout pour les personnes plus jeunes.
                      Cependant, les primes peuvent augmenter lors du renouvellement.
                    </td>
                    <td className="p-4">
                      <span className="font-semibold text-[#20a7df]">
                        Les primes d'assurance permanente
                      </span>{" "}
                      sont généralement plus élevées que celles de l'assurance temporaire, mais
                      restent stables tout au long de la vie du titulaire de la police.
                    </td>
                  </tr>

                  <tr className="border-t">
                    <td className="p-4 font-semibold text-[#49628b]">Prestation de décès</td>
                    <td className="p-4">
                      La prestation de décès n'est versée que si l'assuré décède pendant la durée
                      de la police. Si le terme expire et que l'assuré est encore en vie, il n'y a
                      aucun versement.
                    </td>
                    <td className="p-4">
                      L'assurance permanente comprend une prestation de décès versée aux
                      bénéficiaires au décès de l'assuré, quel que soit son âge.
                    </td>
                  </tr>

                  <tr className="border-t">
                    <td className="p-4 font-semibold text-[#49628b]">Valeur de rachat</td>
                    <td className="p-4">
                      <span className="font-semibold text-[#20a7df]">Les polices d'assurance temporaire</span>{" "}
                      n'accumulent pas de valeur de rachat. Ce sont de pures protections sous forme
                      de prestation de décès sans composante épargne ou placement.
                    </td>
                    <td className="p-4">
                      <span className="font-semibold text-[#20a7df]">L'assurance permanente</span>{" "}
                      accumule une valeur de rachat au fil du temps. Cette valeur de rachat peut être
                      accessible par des retraits ou des prêts pendant la vie de l'assuré.
                    </td>
                  </tr>

                  <tr className="border-t">
                    <td className="p-4 font-semibold text-[#49628b]">Idéal pour</td>
                    <td className="p-4">
                      Les personnes cherchant une couverture abordable pour une période spécifique,
                      comme les jeunes familles, les personnes avec des responsabilités financières
                      temporaires ou celles qui ont un budget limité.
                    </td>
                    <td className="p-4">
                      Les personnes cherchant une couverture à vie, la planification successorale,
                      celles intéressées par une composante épargne ou placement, ou celles qui
                      veulent une police qui accumule une valeur de rachat au fil du temps.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="flex justify-center mt-10 sm:mt-12">
              <a
                href="https://compulife.org/website/655/quoter.html"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto bg-sky-500 hover:bg-sky-600 text-white font-semibold px-8 py-3 rounded-full transition text-center inline-block"
              >
                Obtenez votre meilleur taux
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* WHY TERM INSURANCE FOR LIABILITIES */}
      <section className="relative bg-[#E6EDF7] border-y-4 border-[#1E48A0] py-16 sm:py-20 px-4 sm:px-6 md:px-24 text-gray-800">
        <div className={`${siteContainer}`}>
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-6 sm:mb-8">
              Pourquoi les personnes ayant d'importants passifs et des hypothèques ont-elles besoin d'une assurance vie temporaire ?
            </h2>

            <p className="text-base sm:text-lg leading-relaxed mb-6">
              Dans un monde rempli d'engagements financiers, comme des hypothèques importantes
              et divers passifs, protéger le bien-être financier de vos proches devient
              primordial.
            </p>

            <p className="text-base sm:text-lg leading-relaxed mb-6">
              C'est là que les{" "}
              <span className="text-sky-600 font-semibold">polices d'assurance temporaire</span>{" "}
              émergent comme une bouée de sauvetage cruciale. Pour les personnes accablées
              par des hypothèques importantes, la question de ce qui arriverait à la maison
              familiale en cas de tragédie imprévue peut être une source de préoccupation
              constante.
            </p>

            <p className="text-base sm:text-lg leading-relaxed mb-6">
              L'assurance vie temporaire offre une solution adaptée en fournissant un filet
              de sécurité qui garantit que votre famille peut continuer à effectuer les
              paiements hypothécaires et à remplir d'autres obligations financières, même si
              vous n'êtes plus là pour subvenir à leurs besoins.
            </p>

            <p className="text-base sm:text-lg leading-relaxed mb-8 sm:mb-10">
              En souscrivant une police d'assurance vie temporaire, vous créez essentiellement
              un bouclier contre les incertitudes de la vie, offrant à vos proches la
              stabilité financière nécessaire pour faire face aux défis qui peuvent survenir.
              Dans les moments de deuil et de perte, la dernière chose dont votre famille
              devrait se préoccuper est la pression financière.{" "}
              <span className="text-sky-600 font-semibold">L'assurance vie temporaire au Canada</span>{" "}
              se présente comme un phare de protection, offrant la tranquillité d'esprit et
              un héritage tangible qui transcende les générations.
            </p>

            <a
              href="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto bg-sky-500 hover:bg-sky-600 text-white font-semibold px-8 py-3 rounded-full transition text-center inline-block"
            >
              Commencer
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 md:px-24 bg-white text-gray-800">
        <div className={`${siteContainer}`}>
          <div className="max-w-5xl mx-auto text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#49628b]">
              Foire aux questions sur l'assurance temporaire
            </h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border rounded-lg shadow-sm transition-all bg-white"
              >
                <button
                  className="w-full flex justify-between items-center text-left px-5 sm:px-6 py-4 font-semibold text-base sm:text-lg text-gray-900 focus:outline-none"
                  onClick={() => toggleFAQ(index)}
                  aria-expanded={activeIndex === index}
                  aria-controls={`faq-panel-${index}`}
                >
                  {faq.question}
                  {activeIndex === index ? (
                    <Minus className="w-6 h-6 text-gray-700" />
                  ) : (
                    <Plus className="w-6 h-6 text-gray-700" />
                  )}
                </button>

                {activeIndex === index && (
                  <div
                    id={`faq-panel-${index}`}
                    className="px-5 sm:px-6 pb-4 text-gray-700 text-base"
                  >
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <BlogSection title="Derniers articles" mode="related" relatedCount={3} />
    </>
  );
};

export default TermLifeInsurance;
